import React, { type ComponentProps } from 'react'

import {
  DashboardPermissionGuard,
  useDashboardFeatureFlags,
  useDashboardPermissions,
} from '@components'
import {
  Banner,
  ButtonClean,
  ConfirmModal,
  Loader,
  Skeleton,
  Subsection,
  useModalContext,
} from '@fourthwall/components'
import {
  AddSubscriptionModal,
  SubscriptionBadge,
  UpgradeSubscriptionButton,
  getSubscriptionPrice,
  usePastDueSubscriptionErrorBanner,
  useSubscription,
  useTopSubscriptionPlan,
} from '@modules/Dashboard/modules/Subscriptions'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'

import * as S from './SubscriptionPlan.styled'
import { SubscriptionPerksTable } from './components/SubscriptionPerksTable/SubscriptionPerksTable'

export const SubscriptionPlan = () => {
  const { hasPermission } = useDashboardPermissions()
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const { open } = useModalContext()
  const {
    isLoading,
    canUpgradeSubscription,
    activeSubscription,
    pendingSubscription,
    cancelActiveSubscription,
    undoCancelActiveSubscription,
  } = useSubscription()
  const isActiveSubscriptionFree = activeSubscription?.plan.price.value === 0
  const pastDueSubscriptionErrorBanner = usePastDueSubscriptionErrorBanner()
  const { topSubscriptionPlan } = useTopSubscriptionPlan()
  const canUserUpgradeSubscription = hasPermission('subscriptions.edit')

  const openAddSubscriptionModal = () => {
    open(AddSubscriptionModal, {
      source: 'settings_billing',
    })
  }

  const openCancelSubscriptionConfirmModal = () => {
    const title = activeSubscription?.activeTo ? (
      <>
        You will keep access to Pro plan until{' '}
        <TimezonedDateString date={activeSubscription.activeTo} />
      </>
    ) : null

    open(ConfirmModal, {
      title: 'Are you sure you want to cancel your subscription?',
      text: title,
      cancelLabel: 'Keep subscription',
      confirmAppearance: 'destructive',
      confirmLabel: 'Cancel subscription',
      onConfirm: async () => {
        await cancelActiveSubscription()
      },
    })
  }

  const openUndoCancelSubscriptionConfirmModal = () => {
    const title = activeSubscription?.activeTo ? (
      <>
        You will automatically renew Pro plan on{' '}
        <TimezonedDateString date={activeSubscription.activeTo} format="DD MMM, YYYY" />
      </>
    ) : null

    open(ConfirmModal, {
      title: 'Your subscription is scheduled to be canceled. Would you like to undo this?',
      text: title,
      cancelLabel: 'Keep canceled',
      confirmLabel: 'Renew subscription',
      onConfirm: async () => {
        await undoCancelActiveSubscription()
      },
    })
  }

  if (!dashboardFeatureFlags.subscriptions.enabled) return null

  const renderUpgradeContent = () => {
    if (isLoading || !canUpgradeSubscription) return null

    return (
      <>
        <S.SectionDescription>
          {isActiveSubscriptionFree ? <strong>You’re currently on the Free plan. </strong> : ''}
          Upgrade to Pro for just{' '}
          {getSubscriptionPrice({
            subscriptionPlan: topSubscriptionPlan,
            separatorType: 'short',
          })}{' '}
          and get perks to help your brand grow.
        </S.SectionDescription>
        <S.ButtonsContainer>
          <UpgradeSubscriptionButton source="settings_billing" onClick={openAddSubscriptionModal} />
        </S.ButtonsContainer>
      </>
    )
  }

  const renderSubscriptionContent = () => {
    const subscription = pendingSubscription || activeSubscription

    if (isLoading || canUpgradeSubscription || !subscription?.plan) return null

    const getSubscriptionBanners = () => {
      if (pastDueSubscriptionErrorBanner.isActive) {
        return (
          <S.BannerContainer>
            <Banner appearance="danger">{pastDueSubscriptionErrorBanner.content}</Banner>
          </S.BannerContainer>
        )
      }
    }

    const getSubscriptionDescription = () => {
      if (pendingSubscription) {
        return (
          <S.SubscriptionDescription>
            Waiting for payment to be confirmed <Loader size="small" />
          </S.SubscriptionDescription>
        )
      }

      if (
        !pendingSubscription &&
        activeSubscription &&
        activeSubscription.scheduledCancelledAtTheEndOfPeriod
      ) {
        const activeCopy = activeSubscription.activeTo ? (
          <>
            (Subscription active until{' '}
            <TimezonedDateString date={activeSubscription.activeTo} format="DD MMM, YYYY" />)
          </>
        ) : null

        return <>Pending cancellation {activeCopy}</>
      }

      if (!pendingSubscription && activeSubscription && activeSubscription.activeTo) {
        return (
          <S.SubscriptionDescription>
            Next billing on{' '}
            <TimezonedDateString date={activeSubscription.activeTo} format="DD MMM, YYYY" />
          </S.SubscriptionDescription>
        )
      }
    }

    const getPlanButtons = () => {
      const buttons: (ComponentProps<typeof ButtonClean> & { enabled: boolean })[] = [
        {
          enabled: !!(
            !pendingSubscription &&
            activeSubscription &&
            !activeSubscription.scheduledCancelledAtTheEndOfPeriod &&
            !!activeSubscription.plan.recurringPeriod &&
            canUserUpgradeSubscription
          ),
          label: 'Cancel subscription',
          appearance: 'primary',
          onClick: openCancelSubscriptionConfirmModal,
        },
        {
          enabled: !!(
            !pendingSubscription &&
            activeSubscription &&
            activeSubscription.scheduledCancelledAtTheEndOfPeriod &&
            canUserUpgradeSubscription
          ),
          label: 'Reactivate subscription',
          appearance: 'primary',
          onClick: openUndoCancelSubscriptionConfirmModal,
        },
      ]

      const enabledButtons = buttons.filter((button) => button.enabled)

      if (enabledButtons.length === 0) return null

      return (
        <S.ButtonsContainer>
          {enabledButtons.map((buttonProps, index) => {
            return <ButtonClean key={index} {...buttonProps} />
          })}
        </S.ButtonsContainer>
      )
    }

    const getSubscriptionName = () => {
      const isCustom = !!activeSubscription?.plan.custom
      const isFree = activeSubscription?.plan.price.value === 0

      if (isCustom && isFree) {
        return <S.SubscriptionName>You’re currently on the Custom free plan.</S.SubscriptionName>
      }

      return (
        <SubscriptionBadge
          label={`${isCustom ? 'Custom ' : ''}Fourthwall Pro (${getSubscriptionPrice({
            subscriptionPlan: activeSubscription?.plan,
          })})`}
        />
      )
    }

    return (
      <S.SubscriptionContainer>
        {getSubscriptionBanners()}
        {getSubscriptionName()}
        <div>{getSubscriptionDescription()}</div>
        {getPlanButtons()}
      </S.SubscriptionContainer>
    )
  }

  const renderSkeleton = () => {
    if (isLoading) {
      return (
        <Skeleton gap={16}>
          <Skeleton.Shape width="100%" height={21} />
          <Skeleton.Shape width="80%" height={21} />
        </Skeleton>
      )
    }

    return null
  }

  return (
    <DashboardPermissionGuard permission="subscriptions.view">
      <Subsection title="Your plan">
        <S.Content>
          {renderSkeleton()}
          {renderUpgradeContent()}
          {renderSubscriptionContent()}
          <SubscriptionPerksTable />
        </S.Content>
      </Subsection>
    </DashboardPermissionGuard>
  )
}
